@if (windowService.isDesktopRes) {
  <tidy-desktop-header>
    <div class="wrappable-container">
      <tidy-text [header]="'Guests'" />
      <form [formGroup]="searchForm" novalidate action="#">
        <div class="customer-guests-search-controls">
          <tidy-select
            [items]="searchFilterOptions"
            [form]="searchForm.get('filter')"
            formControlName="filter"
            [hideSearch]="true"
            [icon]="selectedSearchFilterIcon"
            (optionChange)="onSearchFilterChange($event)"
            class="customer-guests-filter-select" />
          <tidy-search-input
            formControlName="search"
            [placeholder]="'Search'"
            [icon]="'assets/img/search-header.svg'"
            class="header-search" />
        </div>
      </form>
      <tidy-small-button
        [text]="'Add Guest'"
        [image]="'assets/img/add-button-white.svg'"
        (buttonClick)="goToAddGuest()" />
    </div>
    <div class="non-wrappable-right-aligned-container">
      @if (isFetching && loaded) {
        <ion-spinner name="lines" class="header-loading-spinner" style="margin-right: 15px" />
      }
    </div>
  </tidy-desktop-header>
}

@if (!windowService.isDesktopRes && loaded) {
  <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 8px; width: 100%;">
    <div style="flex: 1; min-width: 0;">
      <form [formGroup]="searchForm" novalidate action="#">
        <div class="customer-guests-search-controls mobile">
          <tidy-select
            [items]="searchFilterOptions"
            [form]="searchForm.get('filter')"
            formControlName="filter"
            [hideSearch]="true"
            [icon]="selectedSearchFilterIcon"
            (optionChange)="onSearchFilterChange($event)"
            class="customer-guests-filter-select" />
          <tidy-search-input
            formControlName="search"
            [placeholder]="'Search'"
            [icon]="'assets/img/search-header.svg'"
            [fixedWidth]="false"
            class="header-search" />
        </div>
      </form>
    </div>
    <div style="flex: 0 0 auto;">
      <tidy-small-button
        [text]="'Add'"
        [image]="'assets/img/add-button-white.svg'"
        (buttonClick)="goToAddGuest()" />
    </div>
  </div>
}

@if ((!loaded || isFetching) && !hasResults() && windowService.isDesktopRes) {
  <tidy-card class="no-padding customer-guests-table-skeleton-card">
    <div class="customer-guests-table-skeleton">
      <div class="customer-guests-table-skeleton-header">
        <span>Name</span>
        <span>Email</span>
        <span>Phone</span>
        <span>Platform</span>
        <span>Added</span>
        <span></span>
      </div>
      @for (_ of skeletonRows; track $index) {
        <div class="customer-guests-table-skeleton-row">
          <div class="customer-guests-table-skeleton-name">
            <ion-skeleton-text animated class="customer-guests-table-skeleton-avatar" />
            <ion-skeleton-text animated style="width: 44%; height: 14px;" />
          </div>
          <ion-skeleton-text animated style="width: 52%; height: 14px;" />
          <ion-skeleton-text animated style="width: 42%; height: 14px;" />
          <ion-skeleton-text animated style="width: 58%; height: 14px;" />
          <ion-skeleton-text animated style="width: 48%; height: 14px;" />
          <ion-skeleton-text animated style="width: 22px; height: 22px; border-radius: 6px;" />
        </div>
      }
    </div>
  </tidy-card>
}

@if ((!loaded || isFetching) && !hasResults() && !windowService.isDesktopRes) {
  @for (_ of mobileSkeletonRows; track $index) {
    <tidy-card class="customer-guests-card-skeleton">
      <div class="customer-guests-card-skeleton-header">
        <ion-skeleton-text animated class="customer-guests-table-skeleton-avatar" />
        <ion-skeleton-text animated style="width: 56%; height: 16px;" />
      </div>
      <ion-skeleton-text animated style="width: 72%; height: 14px;" />
      <ion-skeleton-text animated style="width: 48%; height: 14px;" />
      <ion-skeleton-text animated style="width: 34%; height: 14px;" />
    </tidy-card>
  }
}

@if (loaded && hasResults() && windowService.isDesktopRes) {
  <tidy-card class="no-padding">
    <tidy-table
      [dataSource]="rows"
      [displayedColumns]="headers"
      [columnslabels]="headers"
      [allRowIsClickable]="true"
      [rowsToShow]="pageSize"
      [showColumnLines]="false"
      [infiniteScroll]="true"
      [totalRows]="totalRows"
      [didLoadAllRows]="!hasMore"
      [blockSorting]="true"
      (pageChange)="loadMore()"
      (cellClick)="onTableCellClick($event)" />
  </tidy-card>
}

@if (loaded && hasResults() && !windowService.isDesktopRes) {
  @for (guest of guests; track guest.id) {
    <tidy-card>
      <tidy-card-button
        (action)="goToEditGuest(guest)"
        class="arrow-to-top no-padding">
        <tidy-row>
          <tidy-initials-icon
            [fullName]="guest.name || '—'" />
          <tidy-text
            [header]="guest.name || '—'"
            [translate]="false" />
        </tidy-row>
      </tidy-card-button>
      @if (guest.email) {
        <tidy-row>
          <tidy-text [body]="guest.email" [translate]="false" />
        </tidy-row>
      }
      @if (guest.phone) {
        <tidy-row>
          <tidy-text [helper]="formatPhone(guest.phone)" [translate]="false" />
        </tidy-row>
      }
      <tidy-row class="guest-platform-row">
        @if (badgeFor(guest.platform).icon; as icon) {
          <tidy-image [src]="icon" class="guest-platform-icon" />
        }
        <tidy-text [helper]="badgeFor(guest.platform).label" />
      </tidy-row>
      <tidy-row [alignLastItemRight]="true" class="extra-top-padding guest-meta-row">
        <tidy-text
          [helper]="formatAddedLabel(guest.created_at)"
          [translate]="false" />
        <tidy-image
          [src]="'assets/svg/trash-outline.svg'"
          class="guest-delete-icon"
          (action)="confirmDelete(guest)" />
      </tidy-row>
    </tidy-card>
  }

  @if (hasMore) {
    <ion-infinite-scroll (ionInfinite)="handleInfiniteScroll($event)">
      <ion-infinite-scroll-content
        loadingSpinner="bubbles"
        loadingText="Loading more guests..." />
    </ion-infinite-scroll>
  }
}

@if (loaded && isEmpty()) {
  <tidy-card class="customer-guests-empty-card">
    <div class="customer-guests-empty">
      <div class="customer-guests-empty-illustration">
        <tidy-image
          [src]="'assets/svg/people-green.svg'"
          class="customer-guests-empty-icon" />
      </div>

      <tidy-row [align]="'center'">
        <tidy-text [title]="'No guests yet'" />
      </tidy-row>

      <tidy-row [align]="'center'" class="extra-bottom-padding">
        <tidy-text
          [body]="'Keep guest contacts in one place. Add a guest manually or connect a channel to sync them automatically.'" />
      </tidy-row>

      <tidy-button
        [text]="'Add Your First Guest'"
        [image]="'assets/img/add-button-white.svg'"
        (click)="goToAddGuest()"
        class="primary" />

      <tidy-row [align]="'center'" class="extra-top-padding customer-guests-empty-perks-row">
        <div class="customer-guests-empty-perks">
          <div class="customer-guests-empty-perk">
            <tidy-image [src]="'assets/svg/person-add-outline.svg'" />
            <tidy-text [body]="'Name, email, and phone in one place'" />
          </div>
          <div class="customer-guests-empty-perk">
            <tidy-image [src]="'assets/img/airbnb-logo.svg'" />
            <tidy-text
              [body]="'Synced from Airbnb, Vrbo, and Booking.com'" />
          </div>
          <div class="customer-guests-empty-perk">
            <tidy-image [src]="'assets/svg/mail-outline.svg'" />
            <tidy-text [body]="'Reach out before, during, and after a stay'" />
          </div>
        </div>
      </tidy-row>
    </div>
  </tidy-card>
}

@if (loaded && isEmptySearch()) {
  <tidy-card>
    <tidy-row [align]="'center'">
      <tidy-text [body]="'No results match your search.'" />
    </tidy-row>
  </tidy-card>
}

@if (errorMessage) {
  <tidy-error-message [text]="errorMessage" />
}
